import { Button, ChipModal, ChipModalBody, ChipModalHeader, PublishingTimeCalendar } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useAccess } from "~/core/access";
import { api } from "~/core/api";
import type { DistributionPost, PublishingPlatform } from "~/core/api/distribution";
import { smartDistribution } from "~/core/api/smart-distribution";
import { language as languageName } from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys } from "~/core/query";
import { ChannelPicker } from "~/common/publishing/channel-picker";
import { Async } from "~/core/ui/states";
import { calendarDays, calendarHour, dayKey } from "./calendar-data";
import { type CalendarPlanEntry, calendarPlanEntries, calendarPlanParams } from "./calendar-plans";
import "./publishing.css";

export function PublishingCalendar({
	entityId,
	posts,
	platforms,
	onOpen,
}: {
	entityId: string;
	posts: DistributionPost[];
	platforms: PublishingPlatform[];
	onOpen: (params: Record<string, string>) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [anchor, setAnchor] = useState(() => new Date());
	const [view, setView] = useState<"day" | "week" | "month">("week");
	const [filter, setFilter] = useState<string[]>([]);
	const [filterOpen, setFilterOpen] = useState(false);
	const [unscheduledOpen, setUnscheduledOpen] = useState(false);
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		refetchInterval: 5000,
	});
	const plans = useQuery({
		queryKey: [entityId, "distribution-calendar-plans"],
		queryFn: async () => {
			const [briefs, operations] = await Promise.all([
				smartDistribution.list(entityId),
				smartDistribution.operations(entityId),
			]);
			return { briefs, operations };
		},
		refetchInterval: 5000,
	});
	const rows = calendarPlanEntries(
		plans.data?.briefs ?? [],
		plans.data?.operations ?? [],
		posts,
		tasks.data?.tasks ?? [],
	).filter((row) => !filter.length || row.platforms.some((p) => filter.includes(p.id)));
	const days = calendarDays(anchor, view);
	const unscheduled = rows.filter((row) => !row.date);
	const names = new Map(platforms.map((p) => [p.id, p.name]));
	const channels = (row: CalendarPlanEntry) => row.platforms.map((p) => names.get(p.id) ?? p.name).join(" · ");
	const title = (row: CalendarPlanEntry) =>
		`${pick(locale, [row.legacy ? "Previous schedule" : "Plan", row.legacy ? "历史排期" : "分发计划"])} #${(row.briefId ?? row.id).slice(-8)}${row.language ? ` · ${languageName(locale, row.language)}` : ""}`;
	const status = (row: CalendarPlanEntry) =>
		t(
			row.legacy
				? ["planned", "draft", "recorded", "scheduled"].includes(row.state)
					? `publishCalendar.${row.state}`
					: `publishing.platform.state.${row.state}`
				: `smartDistribution.state.${row.state}`,
		);
	const open = (row: CalendarPlanEntry) => {
		setUnscheduledOpen(false);
		onOpen(calendarPlanParams(row));
	};
	const create = (date?: Date) => onOpen({ tab: "prepare", ...(date ? { scheduled_at: date.toISOString() } : {}) });
	const move = (n: number) =>
		setAnchor((a) =>
			view === "month"
				? new Date(a.getFullYear(), a.getMonth() + n, 1)
				: new Date(a.getFullYear(), a.getMonth(), a.getDate() + n * (view === "week" ? 7 : 1)),
		);
	return (
		<div className="publisher-calendar stack">
			<div className="publisher-calendar-toolbar">
				<div className="platform-actions">
					<Button variant="ghost" aria-label={t("publishCalendar.previous")} onClick={() => move(-1)}>
						<ChevronLeft />
					</Button>
					<strong aria-live="polite">
						{view === "month"
							? anchor.toLocaleDateString(locale, { year: "numeric", month: "long" })
							: `${days[0].toLocaleDateString(locale, { month: "short", day: "numeric" })} — ${days.at(-1)!.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" })}`}
					</strong>
					<Button variant="ghost" aria-label={t("publishCalendar.next")} onClick={() => move(1)}>
						<ChevronRight />
					</Button>
					<Button variant="default" onClick={() => setAnchor(new Date())}>
						{t("publishCalendar.today")}
					</Button>
					<Button variant="outline" onClick={() => setFilterOpen(true)}>
						{filter.length
							? pick(locale, [`${filter.length} channels`, `已选 ${filter.length} 个渠道`])
							: t("publishCalendar.allPlatforms")}
					</Button>
					{filter.length > 0 && (
						<Button variant="ghost" onClick={() => setFilter([])}>
							{pick(locale, ["Clear", "清除"])}
						</Button>
					)}
				</div>
				<div className="platform-actions">
					<fieldset aria-label={t("publishCalendar.view")} className="bare-fieldset platform-actions">
						{(["day", "week", "month"] as const).map((v) => (
							<Button
								key={v}
								variant={view === v ? "default" : "ghost"}
								aria-pressed={view === v}
								onClick={() => setView(v)}
							>
								{t(`publishCalendar.${v}`)}
							</Button>
						))}
					</fieldset>
					<Button variant="ghost" onClick={() => setUnscheduledOpen(true)}>
						{pick(locale, ["Unscheduled plans", "未排期计划"])} · {unscheduled.length}
					</Button>
				</div>
			</div>
			<Async state={plans}>
				{() => (
					<Async state={tasks}>
						{() => (
							<>
								<PublishingTimeCalendar
									label={t("publishCalendar.title")}
									date={anchor}
									view={view}
									locale={locale}
									events={rows
										.filter((row) => row.date)
										.map((row) => ({
											id: row.id,
											title: title(row),
											start: row.date!,
											platform: channels(row),
											status: status(row),
											tone: ["failed", "unknown", "waiting_human"].includes(row.state) ? "attention" : undefined,
											editable: false,
										}))}
									onOpen={(id) => {
										const row = rows.find((row) => row.id === id);
										if (row) open(row);
									}}
									onSelect={
										editor
											? (date, allDay) => {
													const at = calendarHour(date);
													if (allDay) at.setHours(9, 0, 0, 0);
													create(at);
												}
											: undefined
									}
								/>
								{!rows.some((row) => row.date && days.some((day) => dayKey(day) === dayKey(new Date(row.date!)))) && (
									<p className="hint">{t("publishCalendar.empty")}</p>
								)}
							</>
						)}
					</Async>
				)}
			</Async>
			<ChannelPicker
				entityId={entityId}
				open={filterOpen}
				onClose={() => setFilterOpen(false)}
				initialSelection={filter.map((platform_id) => ({ platform_id, format: "" }))}
				allowEmpty
				filterOnly
				confirmLabel={pick(locale, ["Apply channel filter", "应用渠道筛选"])}
				onConfirm={(channels) => {
					setFilter(channels.map((c) => c.platform_id));
					setFilterOpen(false);
				}}
			/>
			<ChipModal
				open={unscheduledOpen}
				onOpenChange={setUnscheduledOpen}
				srTitle={pick(locale, ["Unscheduled plans", "未排期计划"])}
				size="lg"
			>
				<ChipModalHeader onClose={() => setUnscheduledOpen(false)} closeAriaLabel={t("common.close")}>
					{pick(locale, ["Unscheduled plans", "未排期计划"])}
				</ChipModalHeader>
				<ChipModalBody>
					{unscheduled.map((row) => (
						<button key={row.id} type="button" className="publisher-calendar-post" onClick={() => open(row)}>
							<strong>{title(row)}</strong>
							<span>{channels(row)}</span>
							<span className="hint">{status(row)}</span>
						</button>
					))}
					{!unscheduled.length && <p className="hint">{pick(locale, ["No unscheduled plans.", "暂无未排期计划。"])}</p>}
				</ChipModalBody>
			</ChipModal>
		</div>
	);
}
